import { type ExternalToast, toast, type ToasterProps } from "vue-sonner";
import { hasOpenModal } from "@/components/overlay";
import { focusAfterSwap, type FocusTarget } from "@/utils/scroll";

import "vue-sonner/style.css";

// ---- 토스트 포커스(93차 C93-14 · JR93-05) ---------------------------------------------------------------------------------
// 토스트(`Toaster`)는 모달 · 화면 밖 `ol > li` 라 Tab 으로 닿으면 포커스가 토스트로 들어온다. 「되돌리기」 를 누르거나 시간이 되어 토스트가 닫히면 그 요소가 사라져 포커스가
// body 로 떨어졌다(89 ~ 93차 HIGH 단골 · `[ui-focus-lost]`). 토스트에 들어오기 **전** 마지막 요소를 기억해 두었다가 거기로 돌려준다.
/** vue-sonner 토스트 영역(`ol`) — 라이브러리가 주는 표지 */
const TOASTER_SELECTOR = "[data-sonner-toaster]";
/** 토스트 안 요소가 사라진 뒤 포커스가 body 에 남았는지 다시 볼 때까지 기다리는 시간(ms) — 크롬은 요소를 뺄 때 곧바로(같은 작업 안에서) focusout 을 보낸다 */
const TOAST_GONE_CHECK_MS = 50;
/** 토스트에 들어오기 전 마지막 포커스 요소 */
let focusBeforeToast: HTMLElement | null = null;

/** 포커스가 토스트로 닿았다가 나가면서 토스트가 닫혀 body 로 떨어졌으면 들어오기 전 요소(없거나 사라졌으면 화면 제목)로 돌려준다 */
function returnFromToast(): Promise<boolean> {
  return focusAfterSwap(() => focusBeforeToast, { fallback: "main h1" });
}

if (typeof document !== "undefined") {
  document.addEventListener(
    "focusin",
    (event) => {
      const el = event.target;
      if (el instanceof HTMLElement && !el.closest(TOASTER_SELECTOR))
        focusBeforeToast = el;
    },
    true,
  );
  document.addEventListener(
    "focusout",
    (event) => {
      const el = event.target;
      if (
        !(el instanceof HTMLElement) ||
        event.relatedTarget ||
        !el.closest(TOASTER_SELECTOR)
      )
        return;
      // 크롬은 요소가 빠지기 **직전에** focusout 을 보낸다 — 그래서 잠시 뒤 요소가 사라졌고 포커스가 body 일 때만 돌려준다(사용자가 빈 곳을 눌러 떠난 것은 건드리지 않는다).
      window.setTimeout(() => {
        if (el.isConnected) return;
        const active = document.activeElement;
        if (active && active !== document.body) return;
        void returnFromToast();
      }, TOAST_GONE_CHECK_MS);
    },
    true,
  );
}

export const toastSuccess = (message: string, options?: ToasterProps) =>
  toast.success(message, { ...options, duration: 2_000, closeButton: true });

export const toastInfo = (message: string, options?: ToasterProps) =>
  toast.info(message, { ...options, duration: 4_000, closeButton: true });

export const toastWarning = (message: string, options?: ExternalToast) =>
  toast.warning(message, { ...options, duration: 6_000, closeButton: true });

export const toastError = (message: string, options?: ExternalToast) =>
  toast.error(message, { ...options, duration: 10_000, closeButton: true });

/**
 * 하류 영향 · 다음 할 일을 담은 결과 안내(31차 Y31-04). 성공 토스트(2초)에 실리면 읽기 전에 사라지므로 오래(10초) 남기고, `action` 으로 하류 화면
 * 바로가기를 줄 수 있다. 확인 창이 따로 없는 조작(재확정 등)이 이 안내가 유일한 신호일 때 쓴다.
 */
export const toastGuide = (
  message: string,
  action?: { label: string; onClick: () => void },
  options?: ExternalToast,
) =>
  toast.success(message, {
    ...options,
    duration: 10_000,
    closeButton: true,
    ...(action ? { action } : {}),
  });

/**
 * 되돌릴 수 있는 조작(목록에서 빼기 · 선택 해제 등)의 결과 안내(69차 ux-a §4.5). 확인 창 대신 쓰고, 「되돌리기」 버튼을 누르면 `onUndo` 를
 * 부른다. 되돌릴 수 없는 조작(서버 영구 삭제 · 발송)은 이 대신 ConfirmDialog(tone=danger)로 먼저 확인받는다.
 * 기본 6초 — 버튼을 찾아 누를 시간을 주되 다음 조작을 가리지 않게 한다.
 * **모달 · 모달 시트 안에서는 쓰지 않는다**(74차 UXV74-05). 토스트는 `aria-modal` 대화상자 밖(`Toaster`)에 떠서 Tab 가둠이 포커스를
 * 모달로 되돌리고 화면 낭독도 읽지 않는다 — 키보드 사용자는 「되돌리기」 에 닿지 못한 채 6초가 지난다. 모달 안에서는 뺀 자리에
 * 한 줄(`Callout tone="neutral" role="status" :items` + `#itemActions` 의 accent sm 「되돌리기」)을 두고 그 버튼으로 포커스를 옮긴다.
 * 개발 모드에서 모달이 열린 채 부르면 경고한다(동작은 그대로).
 * 93차 C93-14(JR93-05): 되돌린 뒤 포커스 — `options.focusAfterUndo`(예: 되살린 칸)를 주면 `onUndo` 가 끝난 뒤 거기로(`focusAfterSwap`), 안 주면 「되돌리기」 를 누를 때 포커스가
 * 토스트 안이었을 때만 토스트에 들어오기 전 요소로(사라졌으면 화면 제목). 호출 12곳은 기본 동작으로 충분하고, 되살린 줄로 보내고 싶은 화면만 `focusAfterUndo` 를 준다.
 */
export function toastUndo(
  message: string,
  onUndo: () => void | Promise<void>,
  options?: { duration?: number; focusAfterUndo?: FocusTarget },
): void {
  if (import.meta.env.DEV && hasOpenModal())
    console.warn(
      `[toastUndo] 모달이 열린 채 불렸습니다 — 토스트는 모달 밖이라 키보드로 닿지 않습니다. 뺀 자리 한 줄(Callout + #itemActions 「되돌리기」)로 (message: ${message})`,
    );
  // 토스트를 띄운 순간 포커스가 있던 요소(지운 버튼이 아직 남아 있거나, 목록이 이미 포커스를 옮긴 뒤면 그 다음 행)가 가까운 「돌아갈 곳」이다.
  const origin =
    document.activeElement instanceof HTMLElement &&
    document.activeElement !== document.body &&
    !document.activeElement.closest(TOASTER_SELECTOR)
      ? document.activeElement
      : null;
  toast.info(message, {
    duration: options?.duration ?? 6_000,
    closeButton: true,
    action: {
      label: "되돌리기",
      onClick: () => {
        // 눌렀을 때 포커스가 토스트 안이었는지는 누르는 순간에만 안다(토스트는 곧 사라진다).
        const fromToast = !!document.activeElement?.closest(TOASTER_SELECTOR);
        // 되돌리기 실패를 삼키지 않는다 — 처리되지 않은 거절로 남기지 않고 사용자에게 알린다
        Promise.resolve()
          .then(onUndo)
          .then(() => {
            if (options?.focusAfterUndo)
              return focusAfterSwap(options.focusAfterUndo, {
                fallback: () => focusBeforeToast ?? "main h1",
              });
            return fromToast
              ? focusAfterSwap(
                  () => (origin?.isConnected ? origin : focusBeforeToast),
                  { fallback: "main h1" },
                )
              : undefined;
          })
          .catch(() => toastError("되돌리지 못했습니다. 다시 시도해 주세요."));
      },
    },
  });
}
